/**
 * Stage I — Procurement Support panel.
 *   I.1  Material Requisitions        → I1Requisitions
 *   I.2  Technical Bid Evaluation     → I2Tbe
 *   I.3  Vendor Document Register     → I3Vdrl
 *   I.4  Procurement Tracking         → I4Tracker
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

const _fmt = (n, d = 1) => (n == null ? "-"
    : typeof n === "number" ? (Number.isInteger(n) ? n : n.toFixed(d)) : n);

function StatTile({ label, value, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/60 p-3"
          data-testid={testid}>
      <div className="text-2xl font-bold text-neutral-100">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4"
          data-testid={testid}>
      <div className="mb-2 text-sm font-semibold text-neutral-200">{title}</div>
      {children}
    </div>
  );
}

function Table({ cols, rows, testid }) {
  return (
    <div className="overflow-x-auto rounded border border-neutral-800"
          data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-neutral-900 text-neutral-400">
          <tr>{cols.map(([, l]) => <th key={l} className="px-2 py-2 text-left font-medium">{l}</th>)}</tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i} className="border-t border-neutral-800 text-neutral-200">
              {cols.map(([k]) => <td key={k} className="px-2 py-1.5">{_fmt(r?.[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ═══════════════════ I.1 · Requisitions ═══════════════════════ */
function I1Requisitions({ res }) {
  const mrs = res?.mrs_first10 || [];
  const bulks = res?.bulk_mrs || [];
  const longLead = res?.long_lead_items || [];
  const itps = res?.itps_first3 || [];
  const spares = res?.spare_parts_summary || {};
  const practices = res?.procurement_practices || {};
  const vendorSample = res?.vendor_lists_sample || {};
  const doc = res?.sample_mr_document || {};
  return (
    <div className="space-y-4" data-testid="i1-requisitions">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total MRs" value={res?.mrs_count || 0}
                    testid="i1-tile-mrs" />
        <StatTile label="Bulk MRs" value={res?.bulk_mrs_count || 0}
                    testid="i1-tile-bulk" />
        <StatTile label="Long-lead items" value={longLead.length}
                    testid="i1-tile-longlead" />
        <StatTile label="ITPs" value={res?.itps_count || 0}
                    testid="i1-tile-itps" />
      </div>

      <Section title={`Consolidated MRs (first 10 of ${res?.mrs_count})`}
                  testid="i1-mrs-table">
        <Table cols={[
          ["mr_no", "MR #"], ["discipline", "Discipline"],
          ["type", "Type"], ["equipment_type", "Equipment"],
          ["item_count", "Items"], ["description", "Description"],
          ["reference", "Ref"],
        ]} rows={mrs} />
      </Section>

      <Section title={`Long-lead items (${longLead.length}) · sorted by lead time`}
                  testid="i1-longlead-table">
        <Table cols={[
          ["equipment_type", "Equipment"],
          ["lead_time_weeks_max", "Lead (weeks)"],
          ["criticality", "Criticality"],
          ["recommended_po_date", "Recommended PO date"],
          ["latest_dispatch", "Latest dispatch"],
        ]} rows={longLead} />
      </Section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Section title="Vendor list sample (I.1-F5)" testid="i1-vendor-list">
          <div className="text-xs text-neutral-300 mb-2">
            Equipment: <b>{vendorSample.equipment_type}</b> · Country: <b>{vendorSample.country}</b> · <b>{vendorSample.n_vendors}</b> vendors
          </div>
          <div className="space-y-1">
            {(vendorSample.vendors || []).map((v, i) => (
              <div key={i} className="text-[11px] text-neutral-400">
                {i + 1}. <b>{v.name}</b> ({v.certifications})
              </div>
            ))}
          </div>
        </Section>
        <Section title="Spare-parts philosophy (I.1-F7)" testid="i1-spares">
          <div className="grid grid-cols-2 gap-2 text-xs text-neutral-300">
            <div>Capital spares: <b>{spares.capital || 0}</b></div>
            <div>Insurance spares: <b>{spares.insurance || 0}</b></div>
            <div>Commissioning: <b>{spares.commissioning || 0}</b></div>
            <div>2-year spares: <b>{spares.two_year || 0}</b></div>
          </div>
        </Section>
      </div>

      <Section title={`ITP sample (${itps[0]?.equipment_type} · ${itps[0]?.activities?.length} activities)`}
                  testid="i1-itp">
        <Table cols={[
          ["no", "#"], ["activity", "Activity"],
          ["acceptance", "Acceptance"], ["reference", "Reference"],
          ["vendor", "V"], ["client", "C"], ["tpi", "TPI"],
        ]} rows={itps[0]?.activities || []} />
        <div className="mt-2 text-[10px] text-neutral-500">
          Legend: R = Review · H = Hold · W = Witness · TPI: {itps[0]?.TPI_agency}
        </div>
      </Section>

      <Section title="Country-specific procurement practices (I.1-F8)"
                  testid="i1-practices">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-neutral-300">
          <div>Country: <b>{res?.country}</b></div>
          <div>Tendering: <b>{practices.tendering}</b></div>
          <div>Certification: <b>{practices.certification}</b></div>
          <div>Payment: <b>{practices.payment}</b></div>
          <div>Authority: <b>{practices.authority}</b></div>
          {practices.local_content && <div>Local content: <b>{practices.local_content}</b></div>}
        </div>
      </Section>

      <Section title={`Sample MR document (${doc.cover?.mr_no})`}
                  testid="i1-mr-document">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Project: <b>{doc.cover?.project}</b></div>
          <div>Date: <b>{doc.cover?.date}</b> · Rev <b>{doc.cover?.revision}</b></div>
          <div>Scope: <b>{doc.cover?.scope}</b></div>
          <div className="mt-2">
            <b>Attachments:</b> {(doc.attachments || []).join(" · ")}
          </div>
        </div>
      </Section>
    </div>
  );
}

/* ═══════════════════ I.2 · TBE ═══════════════════════════════ */
function I2Tbe({ res }) {
  const tbe = res?.tbe_result || {};
  const cmp = res?.bid_comparison || {};
  const analyses = res?.vendor_analyses || [];
  const report = res?.tbe_report || {};
  const tqs = res?.technical_queries || [];
  return (
    <div className="space-y-4" data-testid="i2-tbe">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Vendors evaluated" value={(tbe.results || []).length}
                    testid="i2-tile-vendors" />
        <StatTile label="Criteria" value={(tbe.criteria || []).length}
                    testid="i2-tile-criteria" />
        <StatTile label="Recommended" value={tbe.recommended_vendor || "-"}
                    testid="i2-tile-recommended" />
        <StatTile label="Technical queries" value={tqs.length}
                    testid="i2-tile-tqs" />
      </div>

      <Section title="Bid comparison matrix (I.2-F2)"
                  testid="i2-comparison-table">
        <div className="overflow-x-auto rounded border border-neutral-800">
          <table className="w-full text-xs">
            <thead className="bg-neutral-900 text-neutral-400">
              <tr>
                <th className="px-2 py-2 text-left">Criterion</th>
                <th className="px-2 py-2 text-left">Weight %</th>
                {(cmp.vendor_names || []).map(v => (
                  <th key={v} className="px-2 py-2 text-left">{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(cmp.rows || []).map((row, i) => (
                <tr key={i} className={row.criterion === "STATUS" ? "border-t border-neutral-700 bg-neutral-900/40 font-semibold" : "border-t border-neutral-800"}>
                  <td className="px-2 py-1.5 text-neutral-200">{row.criterion}</td>
                  <td className="px-2 py-1.5 text-neutral-400">{row.weight_pct}</td>
                  {(cmp.vendor_names || []).map(v => (
                    <td key={v} className="px-2 py-1.5 text-neutral-200">{String(row[v] ?? "-")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[10px] text-neutral-500">
          Recommended: <b className="text-green-400">{cmp.recommended || "-"}</b>
        </div>
      </Section>

      <Section title={`Vendor submission analyses (I.2-F3) · ${analyses.length} vendors`}
                  testid="i2-analyses">
        <Table cols={[
          ["vendor", "Vendor"], ["n_deviations", "Deviations"],
          ["n_missing", "Missing"], ["compliance_pct", "Compliance %"],
        ]} rows={analyses} />
      </Section>

      <Section title={`TBE report (I.2-F4) · ${report.recommended}`}
                  testid="i2-report">
        <div className="text-xs text-neutral-300 space-y-1">
          {(report.sections || []).map(s => (
            <div key={s.no} className="border-l-2 border-neutral-700 pl-2">
              <b>§{s.no} {s.title}</b>: {s.content}
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Technical queries (I.2-F5) · ${tqs.length}`}
                  testid="i2-tqs">
        <Table cols={[
          ["no", "TQ #"], ["vendor", "Vendor"],
          ["reference", "Reference"], ["question", "Question"],
          ["response_by_days", "Days"],
        ]} rows={tqs.slice(0, 10)} />
      </Section>
    </div>
  );
}

/* ═══════════════════ I.3 · VDRL ═══════════════════════════════ */
function I3Vdrl({ res }) {
  const sample = res?.sample_vdrl || {};
  const wf = res?.doc_workflow || {};
  const codes = res?.doc_codes || {};
  const dash = res?.doc_dashboard || {};
  const trn = res?.sample_transmittal || {};
  return (
    <div className="space-y-4" data-testid="i3-vdrl">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="VDRLs created" value={res?.vdrl_count || 0}
                    testid="i3-tile-vdrls" />
        <StatTile label="Total docs" value={dash.total_documents || 0}
                    testid="i3-tile-docs" />
        <StatTile label="Approved %"
                    value={`${dash.completion_pct || 0}%`}
                    testid="i3-tile-approved" />
        <StatTile label="Overdue" value={dash.n_overdue || 0}
                    testid="i3-tile-overdue" />
      </div>

      <Section title="Vendor document review codes (I.3-F3)"
                  testid="i3-codes">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-neutral-300">
          {Object.entries(codes).map(([c, m]) => (
            <div key={c}><b className="text-blue-400">{c}</b>: {m}</div>
          ))}
        </div>
      </Section>

      <Section title={`Sample VDRL · ${sample.mr_no} · ${(sample.vdrl || []).length} documents (I.3-F1)`}
                  testid="i3-sample-vdrl">
        <Table cols={[
          ["doc_no", "Doc #"], ["title", "Title"],
          ["review_code", "Code"], ["required_copies", "Copies"],
          ["return_days", "Return (d)"],
          ["submission_due", "Due"], ["status", "Status"],
        ]} rows={sample.vdrl || []} />
      </Section>

      <Section title="Workflow states (I.3-F2)" testid="i3-workflow">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-300">
          {Object.entries(wf.counts || {}).map(([s, n]) => (
            <div key={s}>{s.replace(/_/g, " ")}: <b>{n}</b></div>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-neutral-400">
          Overdue: <b>{wf.n_overdue || 0}</b> · Typical cycles: <b>{wf.typical_cycles}</b>
        </div>
      </Section>

      <Section title="Documents dashboard (I.3-F4)" testid="i3-dashboard">
        <Table cols={[
          ["mr_no", "MR #"], ["n_total", "Total"],
          ["n_approved", "Approved"], ["n_overdue", "Overdue"],
        ]} rows={dash.per_mr || []} />
      </Section>

      <Section title={`Sample transmittal (I.3-F5) · ${trn.transmittal_no}`}
                  testid="i3-transmittal">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>From: <b>{trn.from}</b> → To: <b>{trn.to}</b></div>
          <div>Subject: {trn.subject}</div>
          <div className="text-[11px] mt-1 text-neutral-400">{trn.action_required}</div>
          <div className="mt-2"><b>Documents:</b></div>
          <ul className="text-[11px] list-disc pl-4">
            {(trn.documents || []).map((d, i) => (
              <li key={i}>{d.doc_no} · {d.title} · Rev {d.revision} · Code {d.purpose}</li>
            ))}
          </ul>
        </div>
      </Section>
    </div>
  );
}

/* ═══════════════════ I.4 · Tracking ═══════════════════════════ */
function I4Tracker({ res }) {
  const tr = res?.procurement_tracker || {};
  const summary = tr.summary || {};
  const entries = tr.entries || [];
  const exp = res?.expediting_report || {};
  const fat = res?.fat_schedule || [];
  const mrr = res?.mrr_template || {};
  const cost = res?.cost_summary || {};
  const sched = res?.procurement_schedule_summary || {};
  const ganttFirst5 = res?.procurement_schedule_gantt_first5 || [];
  return (
    <div className="space-y-4" data-testid="i4-tracker">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Green (on track)" value={summary.green || 0}
                    testid="i4-tile-green" />
        <StatTile label="Yellow (at risk)" value={summary.yellow || 0}
                    testid="i4-tile-yellow" />
        <StatTile label="Red (delayed)" value={summary.red || 0}
                    testid="i4-tile-red" />
        <StatTile label="Blue (complete)" value={summary.blue || 0}
                    testid="i4-tile-blue" />
      </div>

      <Section title={`Procurement tracker · 15 milestones · ${entries.length} POs (I.4-F1)`}
                  testid="i4-tracker-table">
        <Table cols={[
          ["mr_no", "MR #"], ["equipment_type", "Equipment"],
          ["current_milestone", "Current milestone"],
          ["milestone_index", "Step #"],
          ["planned_date", "Planned"], ["delta_days", "Δ days"],
          ["traffic", "Status"],
        ]} rows={entries.slice(0, 20)} />
      </Section>

      <Section title={`FAT schedule (I.4-F3) · ${fat.length} FATs`}
                  testid="i4-fat">
        <Table cols={[
          ["mr_no", "MR #"], ["equipment", "Equipment"],
          ["target_date", "Target"], ["duration", "Duration"],
          ["standard", "Standard"], ["status", "Status"],
        ]} rows={fat} />
      </Section>

      <Section title={`Expediting report (I.4-F2) · ${exp.on_track_pos || 0} on track / ${exp.at_risk_pos || 0} at risk / ${exp.delayed_pos || 0} delayed`}
                  testid="i4-expediting">
        <div className="text-xs text-neutral-300 space-y-1">
          {(exp.sections || []).map(s => (
            <div key={s.no} className="border-l-2 border-neutral-700 pl-2">
              <b>§{s.no} {s.title}</b>: {s.content}
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Cost summary (I.4-F5) · ${_fmt(cost.total_pos_value, 0)} total`}
                  testid="i4-cost">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-neutral-300">
          <div>Total POs: <b>{_fmt(cost.total_pos_value, 0)}</b></div>
          <div>Freight 6%: <b>{_fmt(cost.overheads?.freight_6pct, 0)}</b></div>
          <div>Insurance 1.5%: <b>{_fmt(cost.overheads?.insurance_1_5pct, 0)}</b></div>
          <div>Inspection 2%: <b>{_fmt(cost.overheads?.inspection_2pct, 0)}</b></div>
          <div>Spare parts 5%: <b>{_fmt(cost.overheads?.spare_parts_5pct, 0)}</b></div>
          <div>Sub-total: <b>{_fmt(cost.subtotal_with_overheads, 0)}</b></div>
          <div>Budget variance: <b>{cost.budget_variance_pct}%</b></div>
        </div>
      </Section>

      <Section title={`Procurement schedule (I.4-F6) · ${sched.total_mrs} MRs · ${sched.critical_path_count} critical`}
                  testid="i4-schedule">
        <Table cols={[
          ["mr_no", "MR #"], ["equipment", "Equipment"],
          ["start", "Start"], ["end", "End"],
          ["duration_weeks", "Duration (w)"], ["critical", "Critical?"],
        ]} rows={ganttFirst5} />
      </Section>

      <Section title="MRR template (I.4-F4)" testid="i4-mrr">
        <div className="text-xs text-neutral-300 mb-2">
          MRR #: <b>{mrr.mrr_no}</b> · PO ref: <b>{mrr.po_ref}</b>
        </div>
        <Table cols={[
          ["item", "Check"], ["type", "Type"],
          ["acceptance", "Acceptance"],
        ]} rows={mrr.checks || []} />
      </Section>

      <Section title="Completeness (I.4-F7)" testid="i4-complete">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Status: <b className={res?.completeness?.status === "PASS" ? "text-green-400" : res?.completeness?.status === "REVIEW" ? "text-amber-400" : "text-red-400"}>{res?.completeness?.status}</b> ({res?.completeness?.percent_complete}%)</div>
          <div>Pass: {res?.completeness?.count_pass} / {res?.completeness?.count_total}</div>
          {(res?.completeness?.missing || []).length > 0 && (
            <div className="text-amber-300">Missing: {(res?.completeness?.missing || []).slice(0, 5).join(", ")}</div>
          )}
        </div>
      </Section>
    </div>
  );
}

/* ═════════════════════ Wrapper ════════════════════════════════ */
export default function StageIPanel({ projectId, subStage }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/stage-i/run`,
                                       { country: "IN" });
      setRes(r.data);
    } catch (e) {
      setRes({ error: e.message });
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { run(); }, [run]);

  return (
    <div className="space-y-4" data-testid={`stage-i-panel-${subStage}`}>
      <div className="flex items-center justify-between rounded-md border border-neutral-800 bg-neutral-950 p-3">
        <div>
          <div className="text-sm font-semibold text-neutral-100">Stage I · Procurement Support</div>
          <div className="text-xs text-neutral-400">
            Sub-stage {subStage} · powered by I.1-I.4 gap-closure engines (25 fixes)
          </div>
        </div>
        <button onClick={run} className="btn btn-primary text-xs"
                  data-testid="stage-i-rerun-btn">Re-run pipeline</button>
      </div>

      {loading && (
        <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-500"
              data-testid="stage-i-loading">
          Running Stage I pipeline · MR consolidation · TBE · VDRL · procurement tracking…
        </div>
      )}

      {!loading && res?.error && (
        <div className="rounded-md border border-red-900 bg-red-950/50 p-4 text-xs text-red-300"
              data-testid="stage-i-error">
          Pipeline error: {res.error}
        </div>
      )}

      {!loading && !res?.error && (
        <>
          {subStage === "I.1" && <I1Requisitions res={res} />}
          {subStage === "I.2" && <I2Tbe          res={res} />}
          {subStage === "I.3" && <I3Vdrl         res={res} />}
          {subStage === "I.4" && <I4Tracker      res={res} />}
        </>
      )}
    </div>
  );
}
